import React, { useEffect, useState } from 'react';
import { Form, Button, Select, Input, InputNumber, Space, message, Spin } from 'antd';
import axios from 'axios';
import AppLayout from '../../components/AppLayout';
import { useParams, useNavigate } from 'react-router-dom';

interface Product {
  _id: string;
  name: string;
}

const BoMForm: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [components, setComponents] = useState<any[]>([{ product_id: '', qty: 1 }]);

  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();

  const [uom, setUom] = useState('Nos');
  const [quantity, setQuantity] = useState(1);
  const [currency, setCurrency] = useState('INR');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL}/inventory`).then((res) => setProducts(res.data)).finally(() => setLoading(false));

    if (id) {
      setLoading(true);
      axios.get(`${process.env.REACT_APP_API_URL}/manufacturing/bom/${id}`).then((res) => {
        const { product_id, components, bom_no, uom, quantity, currency } = res.data;
        form.setFieldsValue({ product_id, bom_no });
        setComponents(components || []);
        setUom(uom);
        setQuantity(quantity);
        setCurrency(currency);
      }).catch(() => message.error('Failed to load BoM')).finally(() => setLoading(false));
    }
  }, [id]);

  const addComponent = () => {
    setComponents([...components, { product_id: '', qty: 1 }]);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (id) {
        await axios.patch(`${process.env.REACT_APP_API_URL}/manufacturing/bom/${id}`, {
          product_id: values.product_id,
          components,
          uom,
          quantity,
          currency,
        });
        message.success('BoM updated');
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/manufacturing/bom`, {
          product_id: values.product_id,
          components,
          uom,
          quantity,
          currency,
        });
        message.success('BoM created');
      }
      setComponents([{ product_id: '', qty: 1 }]);
      form.resetFields();
      navigate(-1);
    } catch {
      message.error('Failed to save BoM');
    }
  };

  return (
    <AppLayout>
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <h3>{id ? 'Edit Bill of Materials (BoM)' : 'Create Bill of Materials (BoM)'}</h3>
          <Form.Item name="product_id" label="Product to Manufacture" rules={[{ required: true }]}>
            <Select options={products.map(p => ({ label: p.name, value: p._id }))} />
          </Form.Item>

          {id && (
            <Form.Item name="bom_no" label="BoM Number">
              <Input disabled />
            </Form.Item>
          )}

          <Form.Item label="Unit of Measure (UOM)">
            <Select value={uom} onChange={setUom}>
              <Select.Option value="Nos">Nos</Select.Option>
              <Select.Option value="Kg">Kg</Select.Option>
              <Select.Option value="Litre">Litre</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Output Quantity">
            <InputNumber min={1} value={quantity} onChange={(val) => setQuantity(val || 1)} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="Cost Configuration">
            <Select defaultValue="Valuation Rate" disabled />
          </Form.Item>

          <Form.Item label="Currency">
            <Select value={currency} onChange={setCurrency}>
              <Select.Option value="INR">INR</Select.Option>
              <Select.Option value="USD">USD</Select.Option>
            </Select>
          </Form.Item>

          {components.map((c, idx) => (
            <Space key={idx} style={{ display: 'flex', marginBottom: 8 }}>
              <Select
                style={{ width: 200 }}
                placeholder="Component"
                value={c.product_id}
                onChange={(val) => {
                  const copy = [...components];
                  copy[idx].product_id = val;
                  setComponents(copy);
                }}
                options={products.map(p => ({ label: p.name, value: p._id }))}
              />
              <InputNumber
                placeholder="Qty"
                value={c.qty}
                min={1}
                onChange={(val) => {
                  const copy = [...components];
                  copy[idx].qty = val || 1;
                  setComponents(copy);
                }}
              />
              <InputNumber
                placeholder="Rate"
                value={1000}
                prefix="₹"
                disabled
              />
              <InputNumber
                placeholder="Amount"
                value={(c.qty || 1) * 1000}
                prefix="₹"
                disabled
              />
            </Space>
          ))}

          <Form.Item>
            <Button type="dashed" onClick={addComponent}>+ Add Component</Button>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">Save BoM</Button>
          </Form.Item>
        </Form>
      </Spin>
    </AppLayout>
  );
};

export default BoMForm;
